// -------------------------------------------------------------
// File: src/utils/useMixerSocket.jsx
// -------------------------------------------------------------
// Snapshot-driven hook for the Colour-Match Mixer.
//
// • Listens to a single authoritative `mixer:roundState` packet.
// • No optimistic UI — trust the backend.
// • Auto-redeems handshake when phase === 'success'.
// • Emits a top-of-minute heartbeat to keep mobile radios alive.
// -------------------------------------------------------------

import { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import axios from 'axios';
import useGeoSocket from './useGeoSocket';

/* ───────── helpers ───────── */
const WS_URL =
  process.env.REACT_APP_API_URL?.replace(/^http/, 'ws') ||
  'ws://localhost:4000';

const stamp = () =>
  new Date().toLocaleTimeString(undefined, { hour12: false });

export default function useMixerSocket(
  eventId,
  userId,
  isWithinGeoFence,
  opts = {},
  logger = console.log  // Add this parameter
) {
  /* refs & state */
  const socketRef        = useRef(null);
  const hbTimeoutRef     = useRef(null);         // ⏱  aligned heartbeat
  const prevPhaseRef     = useRef('portal');
  const latestTimerRef   = useRef(0);
  const connectedAtRef   = useRef(0);
  const optsRef          = useRef(opts);         // 🆕 stable callbacks

  const [connected, setConnected]   = useState(false);
  const [state, setState]           = useState({ phase: undefined, epoch: 0 });
  const [nextStartsAt, setNextStartsAt] = useState(null);
  const [occupancy, setOccupancy]   = useState(0);
  const [mixerEndedAt, setMixerEndedAt] = useState(null); // 🆕

  const token = localStorage.getItem('accessToken');
  useGeoSocket({ userId, token });

  // keep latest opts without retriggering socket lifecycle
  useEffect(() => { optsRef.current = opts; }, [opts]);

  /* ───────── aligned heartbeat helper ───────── */
  // socket heartbeat with forced reconnect every minute
  const startAlignedHeartbeat = useCallback(() => {
    if (!socketRef.current) return;

    const scheduleNext = () => {
      const now = new Date();
      const msToNextMinute = 60_000 - (now.getSeconds() * 1000 + now.getMilliseconds());

      hbTimeoutRef.current = setTimeout(() => {
        if (socketRef.current?.connected) {
          socketRef.current.emit('heartbeat', { eventId, userId });
          logger(`[${stamp()}] 💓 heartbeat`);
        }
        scheduleNext();
      }, msToNextMinute);
    };

    clearTimeout(hbTimeoutRef.current);
    scheduleNext();
  }, [eventId, userId, logger]);

  /* ───────── socket lifecycle ───────── */
  useEffect(() => {
    if (!eventId || !userId || !isWithinGeoFence) return; // guard

    const socket = io(WS_URL, {
      transports : ['websocket'],                 // force real WS
      auth       : { token: localStorage.getItem('accessToken') },
    });
    socketRef.current = socket;

    /* connection events */
    socket.on('connect', () => {
      logger(`[${stamp()}] ✅ connect → id=${socket.id}`);
      setConnected(true);
      socket.emit('mixer:joinEvent', { eventId, userId });

      connectedAtRef.current = Date.now();

      // Explicitly start aligned heartbeat here
      startAlignedHeartbeat();

      // 🔁 resume AFTER connect so it can’t be lost during handshake
      socket.emit('mixer:resume', { eventId, userId });
    });

    socket.on('disconnect', reason => {
      logger(`[${stamp()}] ❌ disconnect → ${reason}`);
      setConnected(false);
    });

    socket.on('connect_error', err => {
      logger(`[${stamp()}] 🚨 connect_error ${err}`);
    });

    // single listener for roundState (authoritative snapshot)
    socket.off('mixer:roundState');
    socket.on('mixer:roundState', pkt => {
      setState(prev => {
        if (pkt.epoch && prev.epoch && pkt.epoch < prev.epoch) return prev; // avoid time travel
        if (pkt.nextRoundStartsAt) {
          try { setNextStartsAt(new Date(pkt.nextRoundStartsAt)); } catch {}
        }
        return pkt;
      });
    });

    /* timer pushes */
    socket.on('mixer:timerUpdate', pkt => {
      logger(`[${stamp()}] ⏱ timerUpdate ${JSON.stringify(pkt)}`);
      if (pkt.epoch <= latestTimerRef.current) return;
      latestTimerRef.current = pkt.epoch;
      setNextStartsAt(new Date(pkt.newStartAt));
    });

    // handle server kicking user from portal
    socket.on('mixer:accessDenied', ({ reason }) => {
      logger(`[${stamp()}] 🚫 mixer:accessDenied → ${reason}`);
      try { socket.disconnect(); } catch {}
      window.location.assign(`/events/mixer-${eventId}`);
    });

    // 🏁 last round finished → bubble to UI
    socket.on('mixer:ended', (payload = {}) => {
      logger(`[${stamp()}] 🏁 mixer:ended → ${JSON.stringify({
        eventId: payload.eventId, roundId: payload.roundId, endedAt: payload.endedAt
      })}`);
      try { setMixerEndedAt(new Date(payload.endedAt || Date.now())); }
      catch { setMixerEndedAt(new Date()); }
      try { optsRef.current?.onMixerEnded?.(payload); } catch {}
    });

    /* cleanup */
    return () => {
      clearTimeout(hbTimeoutRef.current);
      try { socket.emit('mixer:leaveEvent', { eventId, userId }); } catch {}
      socket.disconnect();
    };
  }, [eventId, userId, isWithinGeoFence, startAlignedHeartbeat]);

  /* ───────── handshake auto-redeem ───────── */
  useEffect(() => {
    if (state.phase !== 'success' || !state.handshake) return;

    axios
      .post(
        `${process.env.REACT_APP_API_URL}/mixer/handshake/${state.handshake}/redeem`,
        { userId },
        { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
      )
      .catch(() => {/* retry on next snapshot */});
  }, [state.phase, state.handshake, userId]);

  /* ───────── phase transition callbacks ───────── */
  useEffect(() => {
    if (!state.phase) {
      return; // ✅ Fixes "destroy is not a function" error
    }

    if (state.phase !== prevPhaseRef.current) {
      console.log(
        `[${stamp()}] 🔄 phase ${prevPhaseRef.current} → ${state.phase}`,
        { epoch: state.epoch, endsAt: state.endsAt }
      );
    }

    if (state.phase === 'portal' && prevPhaseRef.current === 'matching') {
      opts.onUnmatched?.();
    }

    prevPhaseRef.current = state.phase;
  }, [state.phase, state.epoch, state.endsAt, opts]);

  /* ───────── legacy surface (keeps old code compiling) ───────── */
  const currentRound = state.roundNumber
    ? { number: state.roundNumber, endsAt: state.endsAt }
    : null;

  const joinMixer       = useCallback(() => {}, []);
  const leaveMixer      = useCallback(() => {}, []);
  const sendFoundColour = useCallback(() => {}, []);

  return {
    connected,
    nextStartsAt,
    state,
    occupancy,
    mixerEndedAt,               // 🆕 Date | null
    mixerEnded: !!mixerEndedAt, // 🆕 boolean convenience

    /* legacy aliases */
    currentRound,
    colour          : state.colour  ?? null,
    codeValue       : state.code    ?? null,
    matchedWith     : state.partner ?? null,
    icebreakerPrompt: state.icebreakerPrompt ?? '',
    unmatchedMessage:
      state.phase === 'portal' && prevPhaseRef.current === 'matching'
        ? 'No match this round.'
        : '',

    joinMixer,
    leaveMixer,
    sendFoundColour,

    unmatchedBlues  : 0,
    unmatchedGreens : 0,
  };
}
